import { ViewGridIcon } from '@heroicons/react/outline';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import type { ReactNode } from 'react';

import clsxm from '@/lib/clsxm';

import { ChannelLogo, useChannelCatalog } from './catalog';
import type { ChannelKey } from './types';
import { useChannelsT } from './useChannelsT';
import { ArrowGlyph, SectionHeading, tone } from '../primitives';

/** The other channel pages, and the way back to everything agents do. */
export default function ChannelLinks({ current }: { current: ChannelKey }) {
  const { t } = useChannelsT();
  const { t: tc } = useLocale('chatbots');
  const others = useChannelCatalog().filter(({ key }) => key !== current);

  return (
    <section id='channels' className='py-20 sm:py-24'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='flex flex-wrap items-end justify-between gap-6'>
          <SectionHeading
            size='md'
            title={tc('One agent. Every channel.')}
            tail={tc('Same answers, wherever customers find you.')}
          />
          <Link
            href='/chatbots'
            className='group inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200'
          >
            {t('See everything AI agents can do')}
            <ArrowGlyph className='h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5' />
          </Link>
        </div>

        <ul className='mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
          {others.map(({ key, name, summary, href }) => (
            <LinkCard
              key={key}
              href={href}
              icon={<ChannelLogo channel={key} />}
              title={name}
              summary={summary}
            />
          ))}
          <LinkCard
            href='/chatbots#channels'
            icon={
              <ViewGridIcon className='h-5 w-5 text-primary-600 dark:text-primary-500' />
            }
            title={t('Every channel')}
            summary={t(
              'Website chat, phone calls, Zapier and more, all from one agent.',
            )}
          />
        </ul>
      </div>
    </section>
  );
}

function LinkCard({
  href,
  icon,
  title,
  summary,
}: {
  href: string;
  icon: ReactNode;
  title: string;
  summary: string;
}) {
  return (
    <li>
      <Link
        href={href}
        className={clsxm(
          'group flex h-full flex-col rounded-[22px] p-5 transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_24px_50px_-30px_rgba(30,20,90,0.45)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
          tone.surface,
          tone.line,
        )}
      >
        <span className='flex h-10 w-10 items-center justify-center rounded-xl bg-[#f3f3f7] dark:bg-white'>
          {icon}
        </span>
        <span
          className={clsxm(
            'mt-5 flex items-center justify-between gap-2 text-[15px] font-semibold',
            tone.ink,
          )}
        >
          {title}
          <ArrowGlyph className='h-3.5 w-3.5 text-primary-600 opacity-0 transition duration-200 group-hover:opacity-100 dark:text-primary-400' />
        </span>
        <span className={clsxm('mt-2 text-sm leading-6', tone.body)}>
          {summary}
        </span>
      </Link>
    </li>
  );
}
